/**
 * Mapper intake MVP → CreateProjectInput.
 *
 * Frontière sémantique critique : le wizard MVP collecte sa propre forme
 * (`IntakeMvpData`), le backend attend `CreateProjectInput`. Cette fonction
 * fait le pont.
 *
 * Aujourd'hui c'est une transformation 1:1 triviale parce que le wizard MVP
 * est aligné sur le schema universel. Mais on garde le mapper isolé pour 2
 * raisons :
 *
 *   1. Le jour où un module d'intake dynamique tenant-specific arrive, son
 *      mapper `mapDynamicFormResponse(answers, tenantForm) → CreateProjectInput`
 *      vivra à côté. Les deux convergent vers le même `POST /v1/projects`.
 *      Pas de duplication.
 *
 *   2. On peut tester ce mapper en isolation avec Vitest. Si un jour on
 *      ajoute un champ au wizard sans l'ajouter au mapper (oubli), le test
 *      doit échouer. C'est la plus petite barrière anti-régression possible.
 *
 * Règles :
 *   - chaînes vides ("") → undefined (le backend traite undefined comme
 *     "ne pas définir" ; "" déclencherait une validation Zod)
 *   - guarantee_codes par défaut = [] (le schema le requiert via .default([]))
 *   - tags par défaut = [] (idem)
 *   - insurer_codes : on ne met pas de défaut — le backend exige min(1).
 *     Si vide, le mapper renvoie un tableau vide et le backend retournera
 *     422. C'est le comportement voulu (ne pas masquer une erreur de form).
 */

import type { CreateProjectInput } from "@kollectivia/shared";

import type { IntakeMvpData } from "./intakeDraftStorage";

function trimOrUndefined(v: string | undefined): string | undefined {
  if (v === undefined) return undefined;
  const t = v.trim();
  return t === "" ? undefined : t;
}

export function mapIntakeMvpToCreateProject(
  data: IntakeMvpData,
): CreateProjectInput {
  // company_id et title sont les seuls champs strictement requis par le
  // schema Create. Décision (8.B) : si manquants, on envoie une chaîne
  // vide — Zod backend renverra alors un 422 déterministe (Invalid uuid /
  // String must contain at least 1 character). Le form devrait empêcher
  // d'arriver ici via `disabled`, mais le mapper reste pur (pas de throw,
  // sortie déterministe). Ne JAMAIS retourner undefined sur ces deux
  // champs : casserait le typage CreateProjectInput.
  return {
    company_id: (data.company_id ?? "") as string,
    title: (data.title ?? "") as string,

    reference: trimOrUndefined(data.reference),
    broker_id: data.broker_id,
    contact_id: data.contact_id,

    effect_date: data.effect_date || undefined,
    renewal_target_date: data.renewal_target_date || undefined,
    deadline_date: data.deadline_date || undefined,

    insurer_codes: (data.insurer_codes ?? []) as CreateProjectInput["insurer_codes"],
    guarantee_codes: (data.guarantee_codes ?? []) as CreateProjectInput["guarantee_codes"],

    notes: trimOrUndefined(data.notes),
    tags: data.tags ?? [],
  };
}
